import { bridgeActionTypes } from './constants';
import { EpisodeStatus, LockerComputerModalVariant } from '../types';

import type { BridgeEpisodeHint, BridgeGameState, BridgeTaskResultModalData } from '../types';
import type { BridgeAction } from './actions';

const initialState: BridgeGameState = {
	episodes: [],
	episodesLoading: false,
	backgroundMusicEnabled: true,
	loading: false,
	room: null,
	error: null,
	task: null,
	taskLoading: false,
	taskVersion: 0,
	lockerComputerModal: null,
	lockerPasswordError: null,
	lockerAgendaModal: false,
	lockerAgendaData: null,
	roomDialogMessages: null,
	taskFinishResult: null,
	map: null,
	mapLoading: false,
};

const bridgeReducer = (state = initialState, action: BridgeAction): BridgeGameState => {
	switch (action.type) {
		case bridgeActionTypes.BRIDGE_GET_EPISODES_REQUEST:
			return {
				...state,
				episodesLoading: true,
			};

		case bridgeActionTypes.BRIDGE_SET_BACKGROUND_MUSIC_ENABLED:
			return {
				...state,
				backgroundMusicEnabled: action.payload.enabled,
			};

		case bridgeActionTypes.BRIDGE_GET_EPISODES_SUCCESS: {
			if (state.lockerComputerModal === LockerComputerModalVariant.NewLetters) {
				const newFlagsByEpisode = new Map(
					state.episodes.map((ep) => [
						ep.id,
						new Map(ep.hints.map((hint) => [hint.position, hint.new])),
					]),
				);
				const episodes = action.payload.episodes.map((episode) => {
					const newFlags = newFlagsByEpisode.get(episode.id);
					if (!newFlags) return episode;

					return {
						...episode,
						hints: episode.hints.map((hint) => ({
							...hint,
							new: newFlags.get(hint.position) ?? false,
						})),
					};
				});

				return {
					...state,
					episodesLoading: false,
					episodes,
				};
			}

			return {
				...state,
				episodesLoading: false,
				episodes: action.payload.episodes,
			};
		}

		case bridgeActionTypes.BRIDGE_GET_EPISODES_ERROR:
			return {
				...state,
				episodesLoading: false,
			};

		case bridgeActionTypes.BRIDGE_GET_ROOM_REQUEST:
			return {
				...state,
				loading: true,
				error: null,
			};

		case bridgeActionTypes.BRIDGE_GET_ROOM_SUCCESS:
			return {
				...state,
				loading: false,
				room: action.payload.room,
				error: null,
				roomDialogMessages: action.payload.room.messages?.length
					? action.payload.room.messages
					: null,
			};

		case bridgeActionTypes.BRIDGE_GET_ROOM_ERROR:
			return {
				...state,
				loading: false,
				room: null,
				error: action.payload.error,
			};

		case bridgeActionTypes.BRIDGE_GET_TASK_REQUEST:
			return {
				...state,
				taskLoading: true,
				task: null,
			};

		case bridgeActionTypes.BRIDGE_GET_TASK_SUCCESS:
			return {
				...state,
				taskLoading: false,
				task: action.payload.task,
				taskVersion: state.taskVersion + 1,
			};

		case bridgeActionTypes.BRIDGE_GET_TASK_ERROR:
			return {
				...state,
				taskLoading: false,
			};

		case bridgeActionTypes.BRIDGE_OPEN_LOCKER_COMPUTER_MODAL:
			return {
				...state,
				lockerComputerModal: action.payload.variant,
			};

		case bridgeActionTypes.BRIDGE_CLOSE_LOCKER_COMPUTER_MODAL: {
			const episodes = state.episodes.map((episode) =>
				episode.hints.some((hint) => hint.new)
					? {
							...episode,
							hints: episode.hints.map((hint) => ({
								...hint,
								new: false,
							})),
					  }
					: episode,
			);

			return {
				...state,
				lockerComputerModal: null,
				lockerPasswordError: null,
				episodes,
			};
		}

		case bridgeActionTypes.BRIDGE_CHECK_LOCKER_PASSWORD_REQUEST:
			return {
				...state,
				lockerPasswordError: null,
			};

		case bridgeActionTypes.BRIDGE_CHECK_LOCKER_PASSWORD_WRONG:
			return {
				...state,
				lockerPasswordError: action.payload.error,
			};

		case bridgeActionTypes.BRIDGE_CHECK_LOCKER_PASSWORD_SUCCESS:
			return {
				...state,
				lockerPasswordError: null,
				lockerAgendaData: action.payload.agendaData,
			};

		case bridgeActionTypes.BRIDGE_CHECK_LOCKER_PASSWORD_ERROR:
			return {
				...state,
				lockerPasswordError: null,
			};

		case bridgeActionTypes.BRIDGE_CLEAR_ROUTE_STATE:
			return { ...initialState };

		case bridgeActionTypes.BRIDGE_FINISH_TASK_SUCCESS: {
			const { hints, result } = action.payload as {
				hints: BridgeEpisodeHint[];
				result: BridgeTaskResultModalData | null;
			};

			if (!hints || hints.length === 0) {
				return { ...state, taskFinishResult: result };
			}

			const newPositions = new Set(hints.map((hint) => hint.position));

			const episodes = state.episodes.map((episode) => {
				if (episode.status !== EpisodeStatus.Active) return episode;

				const mergedHints = episode.hints.map((hint) =>
					newPositions.has(hint.position)
						? {
								...hint,
								...hints.find((newHint) => newHint.position === hint.position)!,
								new: true,
						  }
						: { ...hint, new: false },
				);

				hints.forEach((newHint) => {
					if (!episode.hints.some((hint) => hint.position === newHint.position)) {
						mergedHints.push({ ...newHint, new: true });
					}
				});

				return { ...episode, hints: mergedHints };
			});

			return {
				...state,
				episodes,
				lockerComputerModal: LockerComputerModalVariant.NewLetters,
				taskFinishResult: result,
			};
		}

		case bridgeActionTypes.BRIDGE_CLEAR_TASK_FINISH_RESULT:
			return { ...state, taskFinishResult: null };

		case bridgeActionTypes.BRIDGE_OPEN_LOCKER_AGENDA_MODAL:
			return {
				...state,
				lockerAgendaModal: true,
			};

		case bridgeActionTypes.BRIDGE_CLOSE_LOCKER_AGENDA_MODAL:
			return {
				...state,
				lockerAgendaModal: false,
				lockerAgendaData: null,
			};

		case bridgeActionTypes.BRIDGE_FINISH_TASK_ERROR:
			return state;

		case bridgeActionTypes.BRIDGE_CLOSE_ROOM_DIALOG:
			return {
				...state,
				roomDialogMessages: null,
			};

		case bridgeActionTypes.BRIDGE_GET_MAP_REQUEST:
			return {
				...state,
				mapLoading: true,
			};

		case bridgeActionTypes.BRIDGE_GET_MAP_SUCCESS:
			return {
				...state,
				mapLoading: false,
				map: action.payload.map,
			};

		case bridgeActionTypes.BRIDGE_GET_MAP_ERROR:
			return {
				...state,
				mapLoading: false,
			};

		default:
			return state;
	}
};

export default bridgeReducer;
